import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { usePharmacy } from '@/context/PharmacyContext';

const navItems = [
  { path: '/admin/dashboard', label: 'Dashboard', icon: 'ri-dashboard-line' },
  { path: '/admin/pharmacy-settings', label: 'Pharmacy Settings', icon: 'ri-store-2-line' },
  { path: '/admin/blog', label: 'Blog Management', icon: 'ri-article-line' },
];

export default function AdminSidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { signOut } = useAuth();
  const { settings } = usePharmacy();

  const handleSignOut = async () => {
    await signOut();
    navigate('/admin/login', { replace: true });
  };

  const isActive = (path: string) => location.pathname === path;

  return (
    <>
      {/* Mobile overlay */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-background-50 border-t border-background-200">
        <div className="flex items-center justify-around py-2 px-4">
          {navItems.map((item) => (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`flex flex-col items-center gap-1 px-3 py-1.5 rounded-lg transition-colors cursor-pointer ${
                isActive(item.path) ? 'text-primary-600' : 'text-foreground-500'
              }`}
            >
              <i className={`${item.icon} text-lg`}></i>
              <span className="text-[10px] font-medium whitespace-nowrap">{item.label}</span>
            </button>
          ))}
          <button
            onClick={handleSignOut}
            className="flex flex-col items-center gap-1 px-3 py-1.5 rounded-lg transition-colors cursor-pointer text-foreground-500"
          >
            <i className="ri-logout-box-line text-lg"></i>
            <span className="text-[10px] font-medium whitespace-nowrap">Sign Out</span>
          </button>
        </div>
      </div>

      {/* Desktop sidebar */}
      <aside
        className={`hidden lg:flex flex-col fixed left-0 top-0 h-screen bg-background-50 border-r border-background-200 z-40 transition-all duration-300 ${
          collapsed ? 'w-[72px]' : 'w-[240px]'
        }`}
      >
        {/* Header */}
        <div className={`flex items-center h-16 px-4 border-b border-background-100 ${collapsed ? 'justify-center' : 'gap-3'}`}>
          {!collapsed && (
            <>
              <div className="w-8 h-8 bg-primary-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <i className="ri-store-2-line text-white text-sm"></i>
              </div>
              <span className="text-sm font-bold text-foreground-950 truncate">{settings?.pharmacy_name || 'Admin'}</span>
            </>
          )}
          {collapsed && (
            <div className="w-8 h-8 bg-primary-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-store-2-line text-white text-sm"></i>
            </div>
          )}
        </div>

        {/* Nav links */}
        <nav className="flex-1 py-4 px-3 space-y-1">
          {navItems.map((item) => (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                isActive(item.path)
                  ? 'bg-primary-50 text-primary-700'
                  : 'text-foreground-600 hover:bg-background-100'
              } ${collapsed ? 'justify-center' : ''}`}
              title={collapsed ? item.label : undefined}
            >
              <i className={`${item.icon} text-lg flex-shrink-0`}></i>
              {!collapsed && <span>{item.label}</span>}
            </button>
          ))}
        </nav>

        {/* Bottom */}
        <div className="border-t border-background-100 p-3 space-y-2">
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap ${
              collapsed ? 'justify-center' : ''
            }`}
            title={collapsed ? 'View Site' : undefined}
          >
            <i className="ri-external-link-line text-lg flex-shrink-0"></i>
            {!collapsed && <span>View Site</span>}
          </a>

          <button
            onClick={handleSignOut}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-accent-600 hover:bg-accent-50 transition-colors cursor-pointer whitespace-nowrap ${
              collapsed ? 'justify-center' : ''
            }`}
            title={collapsed ? 'Sign Out' : undefined}
          >
            <i className="ri-logout-box-line text-lg flex-shrink-0"></i>
            {!collapsed && <span>Sign Out</span>}
          </button>
        </div>

        {/* Collapse toggle */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 bg-background-50 border border-background-200 rounded-full flex items-center justify-center cursor-pointer hover:bg-background-50 transition-colors z-50"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <i className={`${collapsed ? 'ri-arrow-right-s-line' : 'ri-arrow-left-s-line'} text-xs text-foreground-500`}></i>
        </button>
      </aside>
    </>
  );
}